import {
  call, put, select, takeEvery, all, delay, takeLatest,
} from 'redux-saga/effects';
import { get } from 'lodash';
import callAdapter, { batchAdapter, callAdapterResponseError } from '@/utils/sagaHelper';
import findResult, {
  findWithReadStatus,
  findWithCodes,
  removeEmpty,
  successAndErrorsOnDestroy,
  successAndErrors,
  createOrderByParams,
} from '@/utils/resultsHelper';
import { wildCardFilterWithSuggestions } from '@/utils/suggestionsHelper';
import { states } from '@/constants/lifecycle';
import urls, { codeUrl } from '@/constants/urls';
import selectors from '@/selectors';
import { CARDS_PAGE_SIZE, presentationOptions } from '@/constants/states';
import { fetchMetrics } from '@/metrics/actions';
import { fetchDatasetMetrics } from '@/datasets/metrics/actions';
import SystemCohortService from '@/cohorts/system/services/systemCohortService';
import { uniqueDatasetCodes } from '@/datasets/dictionaries/helper';
import { workspaceForms } from '@/workspaces/constants';
import WorkflowsService from '@/workflows/service';
import actions, {
  REQUEST_REVIEW_SUGGESTIONS,
  REQUEST_SUGGESTIONS,
  requestLists,
  requestSourceEntities,
  requestSources,
} from '../constants';
import RequestsService from '../service';
import {
  showRequestsResults,
  showRequestsError,
  showRequest,
  showRequestError,
  newRequestSuccess,
  newRequestError,
  approveRequestSuccess,
  approveRequestError,
  denyRequestSuccess,
  denyRequestError,
  destroyRequests,
  destroyRequestsError,
  transferRequestError,
  transferRequestSuccess,
  requestPollingComplete,
  showCloneRequest,
  showRequestSourceError,
  showRequestWorkflowStatus,
  showRequestWorkflowStatusError,
  showWorkflowRequestError,
  showWorkflowForRequest,
} from '../actions';
import { fetchRequestDictionaries } from '../datasets/actions';
import { fetchRequestQueries } from '../cohorts/actions';

function* refreshDatasetMetrics(datasetCodes) {
  const datasetMetrics = yield select(selectors.datasetMetrics);
  const metricsToRefresh = removeEmpty(datasetCodes.map((code) => (findResult(datasetMetrics, 'open', code))));
  yield all(metricsToRefresh.map((metric) => put(fetchDatasetMetrics('open', metric.code))));
}

export function* requests(action) {
  const items = yield select(selectors.requests);
  const {
    display, filter, reviewFlag, pageSize, currentPage, pendingFlag, approvedFlag, deniedFlag, errorFlag, orderBy,
  } = items[action.category];

  const review = action.reviewFlag || reviewFlag;
  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const includePending = action.pendingFlag || pendingFlag;
  const includeApproved = action.approvedFlag || approvedFlag;
  const includeDenied = action.deniedFlag || deniedFlag;
  const includeErrors = action.errorFlag || errorFlag;
  const orderByParams = createOrderByParams(orderBy);
  const { response, error } = yield call(callAdapter, RequestsService.requests(
    wildCardFilterWithSuggestions(filter, review ? REQUEST_REVIEW_SUGGESTIONS : REQUEST_SUGGESTIONS),
    includePending,
    includeApproved,
    includeDenied,
    includeErrors,
    review,
    page,
    display === presentationOptions.CARDS ? CARDS_PAGE_SIZE : size,
    orderByParams,
  ));

  if (error) {
    yield put(showRequestsError(action.category, error));
  } else {
    yield put(showRequestsResults(
      action.category,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

function* afterDestroy(items, results, isArchival = false) {
  const { success, errors } = successAndErrorsOnDestroy(results, 'Request not found');
  if (errors.length > 0) {
    yield put(destroyRequestsError(errors, isArchival));
  }

  if (success.length > 0) {
    const requestItems = yield select(selectors.requests);
    const codes = success.map((item) => item.code);
    const categories = findWithCodes(requestItems, requestLists, codes);
    const toRefresh = findWithReadStatus(requestItems, categories, states.SUCCESS);

    yield put(destroyRequests(success, isArchival, false));
    yield all(toRefresh.map((category) => requests({ category })));

    yield put(fetchMetrics(['requests']));
    yield refreshDatasetMetrics(uniqueDatasetCodes(items));
  }
}

export function* request({ category, code }) {
  const { response, error } = yield call(callAdapter, RequestsService.request(code));
  if (error) {
    yield put(showRequestError(category, code, error));
  } else {
    yield put(showRequest(category, code, response));
  }
}

export function* pollRequest({ category, code }) {
  for (let i = 5; i >= 1; i--) {
    const { response, error } = yield call(callAdapter, RequestsService.workflowStatus(code));
    if (error) {
      yield put(showRequestWorkflowStatusError(category, code, error));
    } else {
      yield put(showRequestWorkflowStatus(category, code, response));
    }

    if (error || !response.waiting) { break; }
    yield delay(2000);
  }
  yield put(requestPollingComplete(category, code));
}

function* cloneCohortRequest({ category, code, cohortCode }) {
  const { response, error } = yield call(callAdapter, SystemCohortService.cohort(cohortCode));

  if (error) {
    yield put(showRequestSourceError(category, code, error));
  } else {
    const names = response.queries.map((query) => query.name);
    yield put(fetchRequestQueries(category, code, response.user_cohort.code, names));
  }
}

function* cloneRequest(category, code, requestSourceCode) {
  const { response, error } = yield call(callAdapter, RequestsService.request(requestSourceCode));

  if (error) {
    yield put(showRequestSourceError(category, code, error));
  } else {
    yield put(showCloneRequest(category, code, response));
    if (response.request_source === requestSources.DATASET) {
      yield put(fetchRequestDictionaries(category, code, response.datasets[0]?.code));
    } else if (response.request_source === requestSources.COHORT) {
      yield call(cloneCohortRequest, { category, code, cohortCode: response.code });
    }
  }
}

export function* newRequestFromSource({
  category,
  code,
  requestSourceType,
  requestSourceCode,
}) {
  if (!requestSourceCode) { return; }

  const decodedCode = decodeURIComponent(requestSourceCode);
  if (requestSourceType === requestSourceEntities.DATASET) {
    yield put(fetchRequestDictionaries(category, code, decodedCode));
  } else if (requestSourceType === requestSourceEntities.COHORT) {
    yield put(fetchRequestQueries(category, code, decodedCode, []));
  } else if (requestSourceType === requestSourceEntities.REQUEST) {
    yield call(cloneRequest, category, code, decodedCode);
  }
}

function* resolveRequestFormSections(item) {
  const workflowKey = item.steps.results[0].settings.workflow_key;
  const workflows = yield select(selectors.workflows);
  const { sections } = findResult(workflows, 'open', workflowKey);

  if (!item.workspace_requested) {
    return sections;
  }

  const forms = yield select(selectors.workspaceForms);
  const form = findResult(forms, 'open', workspaceForms.REQUEST);
  return { ...sections, ...form.sections };
}

export function* create({ category, code }) {
  const items = yield select(selectors.requests);
  const item = findResult(items, category, code);
  const sections = yield* resolveRequestFormSections(item);
  const { response, error } = yield call(callAdapter, RequestsService.create(item, sections));

  if (error) {
    yield put(newRequestError(category, code, error));
  } else {
    const previousUrl = item.steps.sourceUrl || codeUrl(urls.REQUEST, code);
    const toRefresh = findWithReadStatus(items, requestLists, states.SUCCESS);

    yield put(newRequestSuccess(category, code, response, previousUrl));
    yield all(toRefresh.map((c) => requests({ category: c })));

    yield put(fetchMetrics(['requests']));
    yield refreshDatasetMetrics([get(response, 'datasets[0].code')]);
  }
}

export function* approve({ items, reason }) {
  const results = yield all(items.map((item) => batchAdapter(item, RequestsService.approve(item.code, reason))));
  const { success, errors } = successAndErrors(results);

  if (errors.length > 0) {
    yield put(approveRequestError(errors));
  }
  if (success.length > 0) {
    yield put(approveRequestSuccess(success));

    yield put(fetchMetrics(['requests']));
    yield refreshDatasetMetrics(uniqueDatasetCodes(items));
  }
}

export function* deny({ items, reason }) {
  const results = yield all(items.map((item) => batchAdapter(item, RequestsService.deny(item.code, reason))));
  const { success, errors } = successAndErrors(results);

  if (errors.length > 0) {
    yield put(denyRequestError(errors));
  }
  if (success.length > 0) {
    yield put(denyRequestSuccess(success));

    yield put(fetchMetrics(['requests']));
    yield refreshDatasetMetrics(uniqueDatasetCodes(items));
  }
}

export function* transfer({ items, automatic }) {
  const results = yield all(items.map((item) => batchAdapter(item, RequestsService.transfer(item.code, automatic))));
  const { success, errors } = successAndErrors(results);

  if (errors.length > 0) {
    yield put(transferRequestError(errors, automatic));
  }
  if (success.length > 0) {
    yield put(transferRequestSuccess(success, automatic));
  }
}

export function* destroy({ items }) {
  const results = yield all(items.map((i) => callAdapterResponseError(i, RequestsService.destroy(i))));
  yield afterDestroy(items, results);
}

export function* archive({ items, reason }) {
  const results = yield all(items.map((i) => (callAdapterResponseError(i, RequestsService.archive(i, reason)))));
  yield afterDestroy(items, results, true);
}

export function* requestForTransfer(action) {
  yield call(request, { category: 'transfer', code: action.code });
}

export function* refreshRequestsPresentation(action) {
  yield call(requests, { ...action, page: 1, append: false });
}

export function* workflowForRequest({ category, code, requestCode }) {
  const { response, error } = yield call(callAdapter, WorkflowsService.workflow(code));
  if (error) {
    yield put(showWorkflowRequestError(category, code, requestCode, error));
  } else {
    yield put(showWorkflowForRequest(category, code, requestCode, response));
  }
}

export default function* requestsApi() {
  yield takeEvery([
    actions.FETCH_REQUESTS,
    actions.REFRESH_REQUESTS,
    actions.UPDATE_REQUESTS_FLAGS,
    actions.TOGGLE_REQUEST_COLUMN_ORDER_BY,
    actions.TOGGLE_REQUEST_SINGLE_SORTING,
  ], requests);
  yield takeEvery(actions.NEW_REQUEST_FROM_SOURCE, newRequestFromSource);
  yield takeEvery(actions.FETCH_REQUEST, request);
  yield takeEvery(actions.POLL_REQUEST, pollRequest);
  yield takeEvery(actions.APPROVE_REQUEST, approve);
  yield takeEvery(actions.DENY_REQUEST, deny);
  yield takeEvery(actions.CREATE_REQUEST, create);
  yield takeEvery(actions.DELETE_REQUESTS, destroy);
  yield takeEvery(actions.TRANSFER_REQUEST, transfer);
  yield takeEvery(actions.ARCHIVE_REQUESTS, archive);
  yield takeLatest(actions.SHOW_TRANSFER_REQUEST, requestForTransfer);
  yield takeEvery(actions.TOGGLE_REQUESTS_PRESENTATION, refreshRequestsPresentation);
  yield takeEvery(actions.FETCH_WORKFLOW_FOR_REQUEST, workflowForRequest);
}
